import React from 'react';
import { map } from 'lodash';
import type { ComponentMap } from '@atlassian/forge-ui';
import {
    FORGE_REQUEST_CREATE,
    PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
    TOP_LEVEL_COMPONENTS,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { ExtensionPointProvider } from '@atlassian/help-center-common-component/forge-ui/extension-point-provider';
import { ExtensionProvider } from '@atlassian/help-center-common-component/forge-ui/extension-provider';
import { Renderer } from '@atlassian/help-center-common-component/forge-ui/renderer';
import { TopLevelComponent } from '@atlassian/help-center-common-component/forge-ui/top-level-component';
import type {
    Extension,
    ExtensionData,
    ForgeRequestCreatePropertyFormData,
    ForgeAppRequestCreatePropertyData,
    WithExtensionListProps,
    ForgeDoc,
    ForgeDocProps,
    ForgeRequestCreatePropertyFields,
    WithBlockedAppsBannerProps,
} from '@atlassian/help-center-common-component/forge-ui/types';
import { withExtensionList } from '@atlassian/help-center-common-component/forge-ui/with-extension-list';
import { requestCreatePropertyPanelUIKitComponents } from '@atlassian/help-center-common-component/forge-ui-components';
import { useShowBanner } from '@atlassian/help-center-common-util/blocked-apps-banner';

interface RequestCreatePropertyPanelModuleProps extends WithExtensionListProps {
    portalId: number;
    requestTypeId: number;
    onUpdateRequestPropertyData: (data: ForgeRequestCreatePropertyFormData) => void;
}

export interface ForgeRequestCreateProps {
    extension: Extension;
    extensionData: ExtensionData;
    onUpdate: (data: ForgeRequestCreatePropertyFormData) => void;
}

export const ForgeRequestCreatePropertyReactFragment: React.FC<ForgeRequestCreateProps> = ({
    extension,
    extensionData,
    onUpdate,
}) => {
    const submit = (payload: ForgeAppRequestCreatePropertyData) => {
        const extId = extension.id.split('/');
        onUpdate({ data: payload.data, extensionId: extId[1] });
        return true;
    };

    const submitUIKit = React.useCallback(
        (forgeDoc: ForgeDoc) => {
            const extId = extension.id.split('/');
            const forgeDocProps: ForgeDocProps = forgeDoc?.children[0].props as unknown as ForgeDocProps;
            if (forgeDocProps?.fieldValue?.updated) {
                const val = forgeDocProps.fieldValue.value?.data;
                const result = map(val, (value, key) => ({ key, value })) as ForgeRequestCreatePropertyFields[];
                onUpdate({
                    data: { fields: result, isValid: forgeDocProps.fieldValue.value?.isValid || false },
                    extensionId: extId[1],
                });
            }
        },
        [extension.id, onUpdate]
    );

    const getComponents = React.useCallback(
        (defaults: ComponentMap) => ({
            ...defaults,
            ...requestCreatePropertyPanelUIKitComponents,
            [TOP_LEVEL_COMPONENTS[PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE]]: TopLevelComponent,
        }),
        []
    );

    return (
        <React.Fragment key={extension.id}>
            <ExtensionProvider extension={extension}>
                <Renderer
                    extension={extension}
                    extensionData={extensionData}
                    getComponents={getComponents}
                    bridge={{ submit }}
                    onSubmit={submitUIKit}
                />
            </ExtensionProvider>
        </React.Fragment>
    );
};

export const RequestCreatePropertyPanelModuleDumb: React.FC<
    RequestCreatePropertyPanelModuleProps & WithBlockedAppsBannerProps
> = ({ extensions, portalId, requestTypeId, onUpdateRequestPropertyData, showBanner, resetBannerState }) => {
    const extensionData: ExtensionData = React.useMemo(
        () => ({
            type: PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
            portal: { id: portalId },
            request: { typeId: requestTypeId },
        }),
        [portalId, requestTypeId]
    );

    const allowedExtensions = useShowBanner(extensions, FORGE_REQUEST_CREATE, true, showBanner, resetBannerState);

    return (
        <>
            {allowedExtensions.length > 0 && (
                <ExtensionPointProvider extensionType={PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE}>
                    {allowedExtensions.map(
                        (extension: Extension) =>
                            extension && (
                                <ForgeRequestCreatePropertyReactFragment
                                    extension={extension}
                                    extensionData={extensionData}
                                    onUpdate={onUpdateRequestPropertyData}
                                    key={extension.id}
                                />
                            )
                    )}
                </ExtensionPointProvider>
            )}
        </>
    );
};

export const RequestCreatePropertyPanel = withExtensionList(PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE)(
    RequestCreatePropertyPanelModuleDumb
);

export default RequestCreatePropertyPanel;
